import React, { forwardRef } from 'react';
import Box from '@mui/material/Box';

import { useSalonPublicTheme } from '../../../context/SalonPublicThemeContext';


const ALIGN_ITEMS = Object.freeze({
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
});

const SalonPublicHero = forwardRef(({
  media,
  eyebrow,
  title,
  description,
  actions,
  children,
  style,
  sx,
  ...props
}, ref) => {
  const { layout, tokens: theme } = useSalonPublicTheme();
  const alignment = layout.tokens.content.alignment;

  return (
    <Box
      {...props}
      ref={ref}
      component="section"
      dir={layout.tokens.direction}
      data-testid="salon-public-hero"
      data-alignment={alignment}
      data-has-media={String(Boolean(media))}
      style={{
        ...style,
        scrollMarginBlockStart: 'var(--salon-public-header-height, 72px)',
      }}
      sx={{
        position: 'relative',
        isolation: 'isolate',
        overflow: 'hidden',
        minHeight: { xs: 320, md: 440 },
        display: 'grid',
        alignItems: 'center',
        backgroundColor: theme.salon.primary,
        color: theme.salon.onPrimary,
        ...sx,
      }}
    >
      {media && (
        <Box
          data-testid="salon-public-hero-media"
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: -2,
            '& > *': { width: '100%', height: '100%', objectFit: 'cover' },
          }}
        >
          {media}
        </Box>
      )}
      {media && (
        <Box
          aria-hidden="true"
          sx={{ position: 'absolute', inset: 0, zIndex: -1, background: 'rgba(0, 0, 0, 0.38)' }}
        />
      )}
      <Box
        data-testid="salon-public-hero-content"
        style={{ alignItems: ALIGN_ITEMS[alignment], textAlign: alignment }}
        sx={{
          width: 'min(100% - 32px, 1200px)',
          mx: 'auto',
          py: { xs: 6, md: 9 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {eyebrow}
        {title}
        {description}
        {actions}
        {children}
      </Box>
    </Box>
  );
});

SalonPublicHero.displayName = 'SalonPublicHero';

export default SalonPublicHero;
